import { useMemo, type ComponentProps, type ReactNode } from "react";
import { Box } from "@mantine/core";
import { DocumentFooter } from "../pathologist-review/DocumentHeader";
import { ReportDocumentSection as DocumentSection } from "../pathologist-review/ReportDocumentSection";
import { LegacyHistoricalFooter } from "../pathologist-review/LegacyHistoricalFooter";
import { historicalPatientName } from "../pathologist-review/legacyHistoricalReportLayout";
import { REPORT_PAGE_LAYOUT } from "../../utils/reportPageLayout";

export type ReportHeaderSectionProps = ComponentProps<typeof DocumentSection>;
export type ReportFooterProps = ComponentProps<typeof DocumentFooter>;

interface ReportAttachmentPageProps {
  children: ReactNode;
  headerSectionProps?: ReportHeaderSectionProps;
  footerProps?: ReportFooterProps;
  imageContent?: boolean;
}

const pageStyle = {
  width: REPORT_PAGE_LAYOUT.widthPx,
  height: REPORT_PAGE_LAYOUT.heightPx,
  overflow: "hidden",
  padding: `${REPORT_PAGE_LAYOUT.marginTopPx}px ${REPORT_PAGE_LAYOUT.marginHorizontalPx}px ${REPORT_PAGE_LAYOUT.marginBottomPx}px`,
  backgroundColor: "var(--mantine-color-white)",
  boxShadow: "var(--mantine-shadow-sm)",
  marginLeft: "auto",
  marginRight: "auto",
  boxSizing: "border-box" as const,
  display: "flex",
  flexDirection: "column" as const,
};

const contentStyle = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "flex-start",
};

const footerStyle = {
  flexShrink: 0,
  backgroundColor: "var(--mantine-color-white)",
};

export function ReportAttachmentPage({
  children,
  headerSectionProps,
  footerProps,
  imageContent,
}: ReportAttachmentPageProps) {
  const historicalFooter = useMemo(() => {
    const data = headerSectionProps?.documentViewData;
    if (!data?.legacyBaseline || !footerProps) return undefined;
    const header = data.legacyReportHeader ?? data.legacyBaseline.header;
    return {
      ...footerProps,
      patientName: historicalPatientName(header),
      caseNumber: header.caseNumber,
    };
  }, [footerProps, headerSectionProps?.documentViewData]);
  return (
    <Box className="pdf-page" mb={16} style={pageStyle}>
      {headerSectionProps && (
        <Box mb={24}>
          <DocumentSection {...headerSectionProps} />
        </Box>
      )}
      <Box
        data-report-attachment-content="true"
        data-report-image-content={imageContent || undefined}
        style={contentStyle}
      >
        {children}
      </Box>
      {footerProps && (
        <Box data-report-attachment-footer="true" style={footerStyle}>
          {historicalFooter ? (
            <LegacyHistoricalFooter {...historicalFooter} />
          ) : (
            <DocumentFooter {...footerProps} />
          )}
        </Box>
      )}
    </Box>
  );
}
